// ─── Client-side encryption for sensitive content ─────────────────────────────
// Everything here runs in the browser using the native Web Crypto API — no
// library, no server involvement. The derived key NEVER leaves this device
// and is NEVER sent to or stored on our servers. We only ever store:
//   - a random salt (not secret — needed to re-derive the same key later)
//   - a small "verification" ciphertext (proves a passphrase is correct
//     without us ever knowing what the passphrase actually is)
//   - the encrypted content itself
//
// If someone loses their Privacy Passphrase, their encrypted history cannot
// be recovered by anyone, including us. That is the point.

const VERIFY_STRING = "frith-passphrase-verify-v1";

function toB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function fromB64(b64) {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer;
}

export function generateSalt() {
  return toB64(crypto.getRandomValues(new Uint8Array(16)));
}

// Derive an AES-GCM key from a passphrase + salt. PBKDF2 with a high
// iteration count makes brute-forcing the passphrase computationally
// expensive even if someone obtained the salt (which isn't secret anyway).
export async function deriveKey(passphrase, saltB64) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    "raw", enc.encode(passphrase), "PBKDF2", false, ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: fromB64(saltB64), iterations: 150000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true, // extractable — needed so we can cache the raw key material for the session
    ["encrypt", "decrypt"]
  );
}

export async function exportKey(key) {
  return toB64(await crypto.subtle.exportKey("raw", key));
}
export async function importKey(rawB64) {
  return crypto.subtle.importKey("raw", fromB64(rawB64), "AES-GCM", true, ["encrypt", "decrypt"]);
}

// ─── Backup code recovery ───────────────────────────────────────────────────
// The actual content-encryption key (CEK) is now generated independently of
// any passphrase — genuinely random, not derived from anything typed. It gets
// "wrapped" (encrypted) twice: once by a key derived from the passphrase, and
// once by a key derived from a randomly-generated backup code. Either one can
// recover the same real CEK. This is the same pattern password managers like
// Bitwarden use for recovery codes — neither path is weaker than the other,
// and losing one doesn't affect the other.
//
// Old accounts created before this existed keep working exactly as before
// (their key IS directly the passphrase-derived key, no wrapping) — this
// function is only used for NEWLY created passphrases from here on.

const BACKUP_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no 0/O/1/I/L — must be handwritten accurately
export function generateBackupCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(20));
  let code = "";
  for (let i = 0; i < 20; i++) {
    code += BACKUP_CODE_ALPHABET[bytes[i] % BACKUP_CODE_ALPHABET.length];
    if (i % 5 === 4 && i < 19) code += "-";
  }
  return code; // e.g. "XK7P9-M3QRT-7WZNB-J4FHY"
}

export async function generateContentKey() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

// Encrypt a plain JS object -> { encrypted, iv } (both base64 strings, safe for Firestore)
export async function encryptObject(key, obj) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const plaintext = enc.encode(JSON.stringify(obj));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return { encrypted: toB64(ciphertext), iv: toB64(iv) };
}

// Decrypt back to the original object. Returns null on failure (wrong key,
// corrupted data) rather than throwing, so callers can degrade gracefully.
export async function decryptObject(key, encryptedB64, ivB64) {
  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromB64(ivB64) }, key, fromB64(encryptedB64)
    );
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    return null;
  }
}

// Create the verification ciphertext stored once, at passphrase creation
export async function createVerifier(key) {
  return encryptObject(key, { v: VERIFY_STRING });
}
// Check a candidate key against the stored verifier
export async function checkVerifier(key, encryptedB64, ivB64) {
  const result = await decryptObject(key, encryptedB64, ivB64);
  return !!result && result.v === VERIFY_STRING;
}
